import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router';

import { api } from '../api/client';
import { useApi } from '../api/useApi';
import { useAuth } from '../auth/AuthContext';
import BouquetEditor from '../components/livetv/BouquetEditor';
import ChannelListPanel from '../components/livetv/ChannelListPanel';
import ReceptionSourcesPanel from '../components/livetv/ReceptionSourcesPanel';

/// One place for every channel from every live TV plugin: where they come from, finding them,
/// correcting them, and putting them into bouquets. Channels come from several plugins at once and
/// no one of them owns the lists people arrange them in, which is why this is the server's page
/// and not a plugin's.
export default function LiveTvChannelsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { can } = useAuth();

  const providers = useApi(['livetv', 'providers'], api.liveTvProviders, { enabled: can('ManageLiveTv') });

  if (!can('ManageLiveTv')) {
    return <Alert severity="error">{t('auth.forbidden')}</Alert>;
  }

  // In the order somebody setting live TV up goes through them: where it comes from - with the
  // channel scan for the sources that tune - then the list every source's channels end up in,
  // then the bouquets people arrange them into. The list is where the page opens.
  const tabs = [
    { path: '/livetv/channels/sources', labelKey: 'livetvChannels.tab.sources' },
    { path: '/livetv/channels/list', labelKey: 'livetvChannels.tab.channels' },
    { path: '/livetv/channels/bouquets', labelKey: 'livetvChannels.tab.bouquets' },
  ];
  const opening = tabs[1].path;

  const current = tabs.find((tab) => location.pathname.startsWith(tab.path))?.path ?? opening;

  return (
    <Stack spacing={3}>
      <Typography variant="h2">{t('livetvChannels.title')}</Typography>

      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={current} onChange={(_, value) => navigate(value)} variant="scrollable" allowScrollButtonsMobile>
          {tabs.map((tab) => (
            <Tab key={tab.path} value={tab.path} label={t(tab.labelKey)} />
          ))}
        </Tabs>
      </Box>

      <Routes>
        <Route index element={<Navigate to={opening} replace />} />
        <Route path="sources" element={<ReceptionSourcesPanel providers={providers.data} />} />
        <Route path="list" element={<ChannelListPanel providers={providers.data} />} />
        <Route path="bouquets" element={<BouquetEditor providers={providers.data} />} />
        <Route path="*" element={<Navigate to={opening} replace />} />
      </Routes>
    </Stack>
  );
}
